import { useMemo, useState } from 'react';
import { GLOSSARY } from '../data/glossary';
import type { GlossaryCategory } from '../data/glossary';
import styles from './GlossaryBrowser.module.css';

interface GlossaryBrowserProps {
  onExit: () => void;
}

const CATEGORY_LABELS: Record<GlossaryCategory | 'all', string> = {
  all: 'all',
  community: 'community',
  dynamic: 'dynamic',
  practice: 'practice',
  safety: 'safety',
  role: 'role',
};

export function GlossaryBrowser({ onExit }: GlossaryBrowserProps) {
  const [filter, setFilter] = useState<GlossaryCategory | 'all'>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return GLOSSARY.filter((t) => {
      if (filter !== 'all' && t.category !== filter) return false;
      if (!q) return true;
      return (
        t.term.toLowerCase().includes(q) ||
        t.definition.toLowerCase().includes(q)
      );
    }).sort((a, b) => a.term.localeCompare(b.term));
  }, [filter, search]);

  return (
    <section className={styles.frame}>
      <header className={styles.header}>
        <span className={styles.label}>glossary · {filtered.length} terms</span>
        <button type="button" className={styles.exit} onClick={onExit}>
          exit
        </button>
      </header>

      <div className={styles.controls}>
        <input
          type="search"
          className={styles.search}
          placeholder="search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.filters}>
          {(['all', 'community', 'dynamic', 'practice', 'safety', 'role'] as const).map((cat) => (
            <button
              key={cat}
              type="button"
              className={`${styles.filter} ${filter === cat ? styles.filterActive : ''}`}
              onClick={() => setFilter(cat)}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>
      </div>

      <dl className={styles.list}>
        {filtered.map((t) => (
          <div key={t.term} className={styles.entry}>
            <dt className={styles.term}>{t.term}</dt>
            <dd className={styles.def}>
              {t.definition}
              <span className={styles.category}>{t.category}</span>
            </dd>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className={styles.empty}>no terms match.</p>
        )}
      </dl>
    </section>
  );
}
